import { useState, useEffect } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Check, X, PencilSimple } from '@phosphor-icons/react';
import { InputCommon } from 'components/common/Input/InputCommon';
import SelectCommon from 'components/common/Select/SelectCommon';
import { ButtonCommon } from 'components/common/Button/ButtonCommon';
import CollapseCommon from 'components/common/Collapse/CollapseCommon';
import { Unit } from 'models';
import { FormInstance } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import { formatMoney } from 'utils';

interface ProductSalePrice {
  minQuantity: number;
  maxQuantity: number;
  note: string;
  salePrice: number;
  unit?: string;
}

type RowType = {
  minQuantity: string;
  maxQuantity: string;
  price: string;
  unit: string;
  note: string;
};

type RowField = keyof RowType;

interface AddUnitCostContentProps {
  form?: FormInstance;
  units?: Unit[];
  isEdits?: boolean;
  isInactive?: boolean;
  buttonName?: string;
  productSalePrices?: ProductSalePrice[];
  setProductSalePrices?: (prices: ProductSalePrice[]) => void;
}

export const AddUnitCostContent = ({
  form,
  isEdits = true,
  buttonName = 'Thêm',
  productSalePrices = [],
  setProductSalePrices,
  units = [],
}: AddUnitCostContentProps) => {
  const [rows, setRows] = useState<RowType[]>([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [saleUnit, setSaleUnit] = useState<Unit | undefined>(undefined);
  const saleUnitId = useWatch('baseUnitId', form);
  useEffect(() => {
    if (saleUnitId) {
      const foundUnit = units.find((unit) => unit.id === saleUnitId);
      if (foundUnit) {
        setSaleUnit(foundUnit);
      } else {
        setSaleUnit(undefined);
      }
    } else {
      setSaleUnit(undefined);
    }
  }, [saleUnitId]);
  useEffect(() => {
    if (productSalePrices && productSalePrices.length > 0) {
      const initialRows = productSalePrices.map((price) => ({
        minQuantity: price.minQuantity.toString(),
        maxQuantity: price.maxQuantity.toString(),
        price: price.salePrice.toString(),
        unit: price.unit || '',
        note: price.note || '',
      }));
      setRows(initialRows);
    }
  }, []);

  useEffect(() => {
    if (setProductSalePrices) {
      const prices = rows.map((row) => ({
        minQuantity: parseInt(row.minQuantity) || 0,
        maxQuantity: parseInt(row.maxQuantity) || 0,
        salePrice: parseFloat(row.price.replace(/,/g, '')) || 0,
        note: row.note,
        unit: row.unit,
      }));
      setProductSalePrices(prices);
    }
  }, [rows, setProductSalePrices]);

  const handleChange = (index: number, field: RowField, value: string) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const handleSave = () => {
    setEditingIndex(-1);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
  };

  const handleRemove = (index: number) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
  };

  const handleAdd = () => {
    setRows([...rows, { minQuantity: '', maxQuantity: '', price: '', unit: '', note: '' }]);
    setEditingIndex(rows.length);
  };

  return (
    <>
      <div className='flex flex-col gap-3'>
        {rows.map((row, index) => {
          const isEditing = editingIndex === index;
          return (
            <div key={index} className='bg-[var(--textBlack2)] flex gap-4 py-1 px-3 rounded-2xl items-center flex-wrap'>
              <div className='min-w-[160px]'>
                {isEditing ? (
                  <>
                    <p className='text-[var(--textBlack56)] h-5'>Số lượng nhỏ nhất</p>
                    <InputCommon
                      twSize='sm2'
                      type='text'
                      inputMode='numeric'
                      placeholder='Nhỏ nhất'
                      value={row.minQuantity}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSave();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          handleChange(index, 'minQuantity', value);
                        }
                      }}
                      className='!px-3 mt-1'
                    />
                  </>
                ) : (
                  <p className='px-3 py-4 text-sm'>
                    Nhỏ nhất: <span className='font-bold'>{row.minQuantity || '—'}</span>
                  </p>
                )}
              </div>

              <div className='min-w-[160px]'>
                {isEditing ? (
                  <>
                    <p className='text-[var(--textBlack56)] h-5'>Số lượng lớn nhất</p>
                    <InputCommon
                      twSize='sm2'
                      type='text'
                      inputMode='numeric'
                      placeholder='Lớn nhất'
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSave();
                        }
                      }}
                      value={row.maxQuantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value)) {
                          handleChange(index, 'maxQuantity', value);
                        }
                      }}
                      className='!px-3 mt-1'
                    />
                  </>
                ) : (
                  <p className='px-3 py-4 text-sm'>
                    Lớn nhất: <span className='font-bold'>{row.maxQuantity || '—'}</span>
                  </p>
                )}
              </div>
              <div className='min-w-[160px]'>
                {isEditing ? (
                  <>
                    <p className='text-[var(--textBlack56)] h-5'>Giá bán</p>
                    <InputCommon
                      twSize='sm2'
                      type='money'
                      placeholder='Giá bán'
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSave();
                        }
                      }}
                      value={row.price}
                      onChange={(e) => handleChange(index, 'price', e.target.value)}
                      className='!px-3 mt-1'
                    />
                  </>
                ) : (
                  <p className='px-3 py-4 text-sm'>
                    Giá: <span className='font-bold'>{formatMoney(row.price) || '—'} VNĐ</span>
                  </p>
                )}
              </div>
              <div className='min-w-[160px]'>
                {isEditing ? (
                  <>
                    <p className='text-[var(--textBlack56)] h-5'>Đơn vị</p>
                    <SelectCommon
                      sizeSelect='md2'
                      className='!bg-white mt-1'
                      placeholderSelect='Chọn đơn vị'
                      disabled={true}
                      options={[{ label: saleUnit?.name || '—', value: saleUnit?.id || '' }]}
                      value={saleUnit?.id || ''}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSave();
                        }
                      }}
                      onChange={(value) => handleChange(index, 'unit', value)}
                    />
                  </>
                ) : (
                  <p className='px-3 py-4 text-sm'>
                    Đơn vị: <span className='font-bold'>{saleUnit?.name || '—'}</span>
                  </p>
                )}
              </div>
              <div className='flex-1 min-w-[200px]'>
                {isEditing ? (
                  <>
                    <p className='text-[var(--textBlack56)] h-5'>Ghi chú</p>
                    <InputCommon
                      twSize='sm2'
                      type='text'
                      placeholder='Ghi chú'
                      value={row.note}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSave();
                        }
                      }}
                      onChange={(e) => handleChange(index, 'note', e.target.value)}
                      className='!px-3 w-full mt-1'
                    />
                  </>
                ) : (
                  <p className='px-3 py-4 text-sm'>
                    Ghi chú: <span className='font-bold'>{row.note || '—'}</span>
                  </p>
                )}
              </div>
              {isEdits && (
                <div className='flex items-center gap-2 min-w-12 mt-5'>
                  {isEditing ? (
                    <>
                      <Check
                        size={20}
                        className='text-[var(--sematicSuccessColor500)] cursor-pointer'
                        onClick={handleSave}
                      />
                      <X
                        size={20}
                        className='text-[var(--sematicErrorColor500)] cursor-pointer'
                        onClick={() => handleRemove(index)}
                      />
                    </>
                  ) : (
                    <PencilSimple className='cursor-pointer' onClick={() => handleEdit(index)} size={20} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isEdits && (
        <div className='mt-3 max-w-fit'>
          <ButtonCommon
            color='basic'
            fullWidth
            radius='rounded'
            size='md'
            className='bg-[var(--bgBlackOpacity)] px-1 justify-start text-black gap-2'
            onClick={handleAdd}
          >
            <PlusOutlined style={{ fontSize: '16px' }} />
            <span className='whitespace-nowrap'>{buttonName}</span>
          </ButtonCommon>
        </div>
      )}
    </>
  );
};

const AddUnitCost: React.FC<AddUnitCostContentProps> = ({
  form,
  units,
  isEdits = true,
  buttonName = 'Thêm',
  productSalePrices = [],
  setProductSalePrices,
  ...props
}) => {
  return (
    <CollapseCommon collapseKey='2' title='Định mức giá bán sỉ/lẻ'>
      <AddUnitCostContent
        form={form}
        units={units}
        isEdits={isEdits}
        buttonName={buttonName}
        productSalePrices={productSalePrices}
        setProductSalePrices={setProductSalePrices}
        {...props}
      />
    </CollapseCommon>
  );
};
export default AddUnitCost;
